import { Alert, AlertProps, Button, ButtonProps, ButtonTypeMap, Stack, Typography } from '@mui/material';
import { ElementType, PropsWithChildren, createContext, useContext, useEffect } from 'react';

import { ErrorIcon, InfoIcon, TipIcon, WarningIcon } from '@/components/StatusIcons';
import { Dismissable, useDismissable } from '@/hooks/useDismissable';
import { NotificationContext, useNotifications } from '@/hooks/useNotifications';
import { blue, borderGrey, cardBgLightGrey } from '@/theme';

export interface InlineAlertProps extends AlertProps {
    severity: 'warning' | 'error' | 'info' | 'tip';
    size?: 'normal' | 'small';
    actionButton?: React.ReactNode;
    children: React.ReactNode;
    dismissNotificationId?: string;
    notificationContext?: NotificationContext;
}

export const AlertContext = createContext({
    severity: 'info' as InlineAlertProps['severity'],
    size: 'normal' as InlineAlertProps['size'],
});

export const InlineAlert = ({
    severity,
    size = 'normal',
    actionButton,
    title,
    dismissNotificationId,
    notificationContext,
    children,
    sx,
    ...props
}: InlineAlertProps) => {
    const { dismissableProps, onDismiss } = useDismissable(dismissNotificationId, notificationContext);

    // going a little off-theme because we're not ready to make "info" gray dashboard-wide
    const isInfo = severity === 'info';

    return (
        <AlertContext.Provider value={{ severity, size }}>
            <Dismissable {...dismissableProps}>
                <Alert
                    variant="outlined"
                    severity={severity}
                    iconMapping={{
                        warning: (
                            <WarningIcon sx={{ width: '1.5rem', height: '1.5rem', aspectRatio: '1/1', mr: 0.5 }} />
                        ),
                        error: <ErrorIcon sx={{ width: '1.5rem', height: '1.5rem', aspectRatio: '1/1', mr: 0.5 }} />,
                        info: <InfoIcon sx={{ width: '1.5rem', height: '1.5rem', aspectRatio: '1/1', mr: 0.5 }} />,
                        tip: <TipIcon sx={{ width: '1.5rem', height: '1.5rem', aspectRatio: '1/1', mr: 0.5 }} />,
                    }}
                    onClose={dismissNotificationId ? onDismiss : undefined}
                    sx={{
                        position: 'relative',
                        color: isInfo ? blue[700] : undefined,
                        backgroundColor: isInfo ? cardBgLightGrey : (theme) => `${theme.palette[severity].main}18`,
                        borderColor: isInfo ? borderGrey : `${severity}.main`,
                        borderRadius: '4px',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        boxSizing: 'border-box',
                        alignItems: 'center',
                        alignContent: 'center',
                        flexWrap: 'wrap',
                        ...(size === 'small'
                            ? { px: 2, py: 0.5 }
                            : {
                                  px: 3,
                                  py: 1.5,
                              }),
                        '.MuiAlert-message': {
                            /* We flex-wrap to allow the button to wrap, but we don't ever want the message to wrap under the icon,
                           so leave enough space for it */
                            maxWidth: props.icon === false ? '100%' : 'calc(100% - 40px)',
                            minWidth: '200px',
                            flex: '1 1 200px',
                            ...(size === 'small' ? { py: 0.5 } : null),
                        },
                        '.MuiAlert-action': {
                            py: 0,
                            pl: 1,
                        },
                        ...sx,
                    }}
                    data-testid="inline-alert"
                    {...props}
                >
                    <Stack
                        direction="row"
                        sx={{ flexGrow: 1, alignItems: 'center', gap: size === 'small' ? 1 : 2, flexWrap: 'wrap' }}
                    >
                        <Stack sx={{ flexGrow: 1, flexBasis: '15em', width: 'min-content' }}>
                            {title && <InlineAlertTitle>{title}</InlineAlertTitle>}
                            {children}
                        </Stack>
                        <Stack
                            direction="row"
                            sx={{
                                gap: 1,
                                alignItems: 'center',
                                ml: 'auto',
                                flexWrap: 'wrap',
                                justifyContent: 'end',
                                ':empty': { display: 'none' },
                            }}
                        >
                            {actionButton}
                        </Stack>
                    </Stack>
                </Alert>
            </Dismissable>
        </AlertContext.Provider>
    );
};

export const InlineAlertTitle = ({ testid, children }: { testid?: string } & PropsWithChildren) => {
    const { size } = useContext(AlertContext);
    return (
        <Typography variant={size === 'small' ? 'h6' : 'h5'} data-testid={`${testid}-title`}>
            {children}
        </Typography>
    );
};

export const InlineAlertBody = ({ testid, children }: { testid?: string } & PropsWithChildren) => {
    return (
        <Typography variant="body2" data-testid={`${testid}-body`}>
            {children}
        </Typography>
    );
};

export const InlineAlertPrimaryButton = <C extends ElementType = ButtonTypeMap['defaultComponent']>({
    testid,
    children,
    sx,
    ...buttonProps
}: { testid?: string } & PropsWithChildren & ButtonProps<C, { component?: C }>) => {
    const { severity } = useContext(AlertContext);
    return (
        <Button
            color={severity}
            size="medium"
            variant="contained"
            sx={{ px: 1.5, py: 0.75, textWrap: 'nowrap', borderColor: 'inherit', ...sx }}
            data-testid={`${testid}-primary-button`}
            {...buttonProps}
        >
            {children}
        </Button>
    );
};

export const InlineAlertSecondaryButton = <C extends ElementType = ButtonTypeMap['defaultComponent']>({
    testid,
    children,
    ...buttonProps
}: { testid?: string } & PropsWithChildren & ButtonProps<C, { component?: C }>) => {
    const { severity } = useContext(AlertContext);
    return (
        <Button
            color={severity}
            size="medium"
            variant="text"
            sx={{ px: 1.5, py: 0.75, textWrap: 'nowrap' }}
            {...buttonProps}
            data-testid={`${testid}-secondary-button`}
        >
            {children}
        </Button>
    );
};

export const DemoInlineAlertCatalog = () => {
    const { undismissNotification } = useNotifications();
    useEffect(() => {
        undismissNotification('demoDismissable-1');
        undismissNotification('demoDismissable-2');
        undismissNotification('demoDismissable-3');
        undismissNotification('demoDismissable-4');
        undismissNotification('demoDismissable-5');
    }, [undismissNotification]);

    const DemoButtons = () => (
        <>
            <InlineAlertSecondaryButton>Secondary action</InlineAlertSecondaryButton>
            <InlineAlertPrimaryButton>Primary action</InlineAlertPrimaryButton>
        </>
    );
    return (
        <Stack gap={2}>
            <InlineAlert severity="error" actionButton={<DemoButtons />}>
                <InlineAlertTitle>Error title</InlineAlertTitle>
                <InlineAlertBody>This is some error text because something has gone terribly wrong.</InlineAlertBody>
            </InlineAlert>
            <InlineAlert severity="warning" actionButton={<DemoButtons />}>
                <InlineAlertTitle>Warning title</InlineAlertTitle>
                <InlineAlertBody>This is some warning text because something is somewhat alarming.</InlineAlertBody>
            </InlineAlert>
            <InlineAlert severity="info" actionButton={<DemoButtons />}>
                <InlineAlertBody>
                    This is some info text because it is important for you to know about this thing.
                </InlineAlertBody>
            </InlineAlert>
            <InlineAlert severity="tip" actionButton={<DemoButtons />}>
                <InlineAlertBody>
                    This is some tip text because we like to be helpful. It also has a bit more text so that we can test
                    wrapping behavior on narrow screens.
                </InlineAlertBody>
            </InlineAlert>
            <InlineAlert severity="error" dismissNotificationId="demoDismissable-1">
                <InlineAlertBody>This one has no actions but is dismissable.</InlineAlertBody>
            </InlineAlert>
            <InlineAlert
                severity="warning"
                dismissNotificationId="demoDismissable-2"
                icon={false}
                actionButton={<InlineAlertPrimaryButton>Fix it please</InlineAlertPrimaryButton>}
            >
                <InlineAlertBody>This one has no icon and only one action.</InlineAlertBody>
            </InlineAlert>
            <InlineAlert severity="info" dismissNotificationId="demoDismissable-3" actionButton={<DemoButtons />}>
                <InlineAlertTitle>Info title</InlineAlertTitle>
                <InlineAlertBody>Dismissable info.</InlineAlertBody>
            </InlineAlert>
            <InlineAlert severity="tip" dismissNotificationId="demoDismissable-4" actionButton={<DemoButtons />}>
                <InlineAlertTitle>Tip title</InlineAlertTitle>
                <InlineAlertBody>Dismissable fun fact.</InlineAlertBody>
            </InlineAlert>
        </Stack>
    );
};
